import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowUpRight, FileText, Sparkles, ShieldCheck, Target, ChevronRight, Layers, Gauge, FileEdit, Wand2, CheckCircle2, CloudUpload, Quote, Star, Tag, Compass, MessagesSquare, Newspaper, GitCompare, BookOpen } from "lucide-react";
import LiveDemoWidget from "@/components/LiveDemoWidget";
import BeforeAfterGallery from "@/components/BeforeAfterGallery";
import RecruiterQuotes from "@/components/RecruiterQuotes";
import EmailCaptureForm from "@/components/EmailCaptureForm";
import ExitIntentModal from "@/components/ExitIntentModal";
import CVShowcase from "@/components/CVShowcase";
import VideoTestimonials from "@/components/VideoTestimonials";
import AnimatedCounter from "@/components/AnimatedCounter";
import CompareTable from "@/components/CompareTable";
import BlogStrip from "@/components/BlogStrip";
import SEO from "@/components/SEO";
import Footer from "@/components/Footer";
import CollapsibleSection from "@/components/CollapsibleSection";
import SectionJumpNav from "@/components/SectionJumpNav";

// Sticky jump-bar items — appear once user scrolls past Pricing CTA.
// Each id matches a CollapsibleSection id below the fold.
const JUMP_ITEMS = [
  { id: "sec-flow", label: "How it works" },
  { id: "sec-before-after", label: "Before / After" },
  { id: "sec-recruiters", label: "Recruiter quotes" },
  { id: "sec-blog", label: "Blog" },
];

const LANDING_FAQ_JSONLD = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [
    {
      "@type": "Question",
      "name": "Is the ResumeLab resume checker free?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Yes. Your first ATS score, line-by-line feedback, and recruiter-grade audit are 100% free, no signup or credit card required. Pro ($20/month) unlocks unlimited audits, AI rewrites, cover letters, and PDF export."
      }
    },
    {
      "@type": "Question",
      "name": "How accurate is ResumeLab's ATS scoring?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Our ATS engine tests against the same 10 parsers used by 99.4% of mid-to-large employers (Workday, Greenhouse, Lever, iCIMS, Taleo, Workable, BambooHR, SmartRecruiters, Jobvite, Recruitee). Average score lift after one audit: +27 points."
      }
    },
    {
      "@type": "Question",
      "name": "Can ResumeLab rewrite my resume with AI?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Pro users get copy-ready AI rewrites of every flagged section — summary, experience bullets, skills, and education — generated by GPT-5.2 with recruiter-grade prompts."
      }
    },
    {
      "@type": "Question",
      "name": "Does ResumeLab work for Kenyan and African job seekers?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Yes. ResumeLab is built for both global and African markets. It handles formats expected by African employers, multinational recruiters, and remote-first roles across Kenya, South Africa, Nigeria, and beyond."
      }
    },
    {
      "@type": "Question",
      "name": "How long does an audit take?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "About 12 seconds. Paste your resume text or upload a PDF/DOCX, and you get a full audit — score, ATS compatibility, line-by-line feedback, and skill gap analysis — in under 15 seconds."
      }
    }
  ]
};

const SEO_BY_VARIANT = {
  default: {
    title: "AI Resume Checker, ATS Scanner & Recruiter Rewrites",
    description: "Score your resume free in 12 seconds. 99.4% ATS compatibility check, line-by-line AI feedback, recruiter-grade rewrites. Built for global + Kenya job seekers.",
  },
  "free-checker": {
    title: "Free Resume Checker — 99.4% ATS Compatible",
    description: "Free AI resume checker. Get an instant ATS score, line-by-line feedback, and skill gap analysis in 12 seconds. No signup required.",
  },
  "ats": {
    title: "ATS Resume Scanner — Beat the Bots in 12 Seconds",
    description: "Test your resume against the same 10 ATS parsers used by 99.4% of employers (Workday, Greenhouse, Lever). Free scan, instant results.",
  },
};

const FEATURE_PILLS = [
  { icon: FileEdit, label: "PROFESSIONAL TEMPLATES", color: "brand-red" },
  { icon: Wand2, label: "AI POWERED SUGGESTIONS", color: "brand-blue" },
  { icon: ShieldCheck, label: "ATS FRIENDLY RESUMES", color: "brand-red" },
  { icon: CloudUpload, label: "EASY EXPORT ANYWHERE", color: "brand-blue" },
];

const VARIANTS = {
  default: {
    overline: "◷ AI Resume Operating System · v1.0",
    h1Top: "Your resume,",
    h1Mid: <>benchmarked</>,
    h1Bottom: "with executive precision.",
    sub: "ResumeLab scores, audits and rewrites your resume the way a senior recruiter would — line by line, with quantified evidence and ATS-grade parsing.",
    cta: "Score my resume",
    title: "ResumeLab — Recruiter-grade resume scoring & AI rewrites",
    meta: "AI-powered resume evaluation with GPT-5.2. Get line-by-line feedback, ATS score, and rewritten bullets in 12 seconds.",
  },
  "free-checker": {
    overline: "◷ FREE RESUME CHECKER · NO SIGNUP",
    h1Top: "Free resume",
    h1Mid: <>checker.</>,
    h1Bottom: "Recruiter-grade.",
    sub: "Get a free, instant resume score with line-by-line feedback. No signup. No card. Powered by GPT-5.2 — the same engine top recruiters use to screen candidates.",
    cta: "Check my resume free",
    title: "Free Resume Checker — Instant AI Score & Feedback | ResumeLab",
    meta: "Free resume checker powered by GPT-5.2. Instant score, ATS compatibility, line-by-line feedback. No signup required.",
  },
  "ats-scanner": {
    overline: "◷ ATS RESUME SCANNER · 99.4% ACCURATE",
    h1Top: "ATS resume",
    h1Mid: <>scanner.</>,
    h1Bottom: "Built like the engines.",
    sub: "75% of resumes never reach a human — the ATS rejects them silently. ResumeLab parses your resume the way Workday, Greenhouse and Lever do, then tells you exactly what to fix.",
    cta: "Scan my resume",
    title: "ATS Resume Scanner — Check ATS Compatibility | ResumeLab",
    meta: "Free ATS resume scanner. Test your resume against Workday, Greenhouse, Lever parsers. Find what gets rejected before recruiters do.",
  },
  "linkedin-audit": {
    overline: "◷ LINKEDIN PROFILE AUDIT · RECRUITER-VIEW",
    h1Top: "Audit your LinkedIn",
    h1Mid: <>the way</>,
    h1Bottom: "recruiters search it.",
    sub: "Your headline, About, Experience and Skills decide whether recruiters click. ResumeLab grades your profile against the recruiter search index — keywords, positioning, and conversion.",
    cta: "Audit my LinkedIn",
    title: "Free LinkedIn Profile Audit — Recruiter Search Optimization | ResumeLab",
    meta: "Audit your LinkedIn profile the way recruiters search it. Headline, About, Skills — AI-powered feedback in 12 seconds.",
  },
};

const STATS = [
  { label: "RESUMES SCORED", value: "120,000+" },
  { label: "AVERAGE LIFT", value: "+27 PTS" },
  { label: "ATS COMPATIBILITY", value: "99.4%" },
  { label: "AVG. ANALYSIS", value: "12 SEC" },
];

const PILLARS = [
  {
    icon: Gauge,
    title: "Forensic Scoring",
    body: "A 100-point composite across Impact, Brevity, Style, Sections and Skills. No vague feedback — every score has evidence.",
  },
  {
    icon: ShieldCheck,
    title: "ATS Engineering",
    body: "We parse the way Workday, Greenhouse and Lever do. Catch invisible formatting bombs before recruiters do.",
  },
  {
    icon: Target,
    title: "Role Targeting",
    body: "Drop a job description and ResumeLab benchmarks your resume against it — keywords, gaps, and the exact lines to rewrite.",
  },
  {
    icon: Sparkles,
    title: "AI Rewrites",
    body: "Strong verbs. Quantified impact. ATS-friendly phrasing. One click turns vague bullets into recruiter magnets.",
  },
];

const STEPS = [
  { n: "01", title: "Upload or paste", body: "PDF, DOCX or raw text. Optionally drop in the job description." },
  { n: "02", title: "ResumeLab analyzes", body: "GPT-5.2 reads your resume the way an executive recruiter would." },
  { n: "03", title: "Score & rewrite", body: "Get a composite score, line-by-line improvements, and AI rewrites." },
];

const FEEDBACK_STORIES = [
  {
    name: "Wanjiru K.",
    role: "Data Analyst → Senior Analyst at Equity Bank",
    avatar: "WK",
    region: "Nairobi, Kenya",
    scoreBefore: 51,
    scoreAfter: 92,
    quote: "I'd applied to 38 jobs with crickets. ResumeLab told me my bullets were all verbs with no numbers. Two rewrites later I had 4 interviews in two weeks — and Equity made me an offer 11 days after I uploaded.",
    win: "+$8,400 annual salary lift",
  },
  {
    name: "Lukas M.",
    role: "Backend Engineer at SAP (Berlin)",
    avatar: "LM",
    region: "Berlin, Germany",
    scoreBefore: 64,
    scoreAfter: 94,
    quote: "I switched from Java backend to a Go/microservices role. ResumeLab caught that my whole resume was tense-mixed and that I'd buried my biggest project on page 2. The German section translation also nailed the SAP vocabulary I needed.",
    win: "3 onsite interviews in 9 days",
  },
  {
    name: "Aaliyah S.",
    role: "Product Manager at Stripe",
    avatar: "AS",
    region: "Lagos → Remote (US)",
    scoreBefore: 58,
    scoreAfter: 96,
    quote: "Three recruiters had ghosted me. ResumeLab benchmarked my CV against a Stripe PM JD and rewrote 6 bullets to lead with revenue impact. Stripe replied to that exact version 4 days later. The ROI on $20 is unreal.",
    win: "Hired remote at 2× salary",
  },
];

export default function Landing({ variant = "default" }) {
  const v = VARIANTS[variant] || VARIANTS.default;
  const { t } = useTranslation();
  const isDefault = variant === "default";
  const seoCfg = SEO_BY_VARIANT[variant] || SEO_BY_VARIANT.default;
  const evalLink = variant === "linkedin-audit" ? "/evaluate?tab=linkedin" : "/evaluate";

  return (
    <main className="text-zinc-900" data-testid="landing-page">
      <SEO
        title={seoCfg.title}
        description={seoCfg.description}
        path={variant === "default" ? "/" : `/${variant}`}
        jsonLd={LANDING_FAQ_JSONLD}
      />
      {/* HERO */}
      <section className="relative border-b border-zinc-200 bg-gradient-to-br from-blue-50 via-fuchsia-50 to-amber-50 overflow-hidden">
        {/* Decorative gradient orbs for energetic warm feel */}
        <div className="absolute -top-32 -right-32 w-96 h-96 bg-gradient-to-br from-red-400/20 to-orange-400/10 blur-3xl rounded-full pointer-events-none" aria-hidden="true" />
        <div className="absolute -bottom-20 -left-20 w-80 h-80 bg-gradient-to-tr from-amber-400/20 to-pink-400/10 blur-3xl rounded-full pointer-events-none" aria-hidden="true" />
        <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-12 pb-16 lg:pt-16 lg:pb-20 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10">
          <div className="lg:col-span-7 fade-up">
            <div className="overline mb-4" data-testid="hero-overline">
              {isDefault ? `◷ ${t("landing.eyebrow")}` : v.overline}
            </div>
            <h1 className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[0.98]" data-testid="hero-h1">
              {isDefault ? (
                <>
                  {t("landing.hero_title_line1")}{" "}<br />
                  <span className="brand-red">{t("landing.hero_title_line2")}</span>
                </>
              ) : (
                <>
                  {v.h1Top}{" "}<br />
                  <span className="brand-red">{v.h1Mid}</span>{" "}{v.h1Bottom}
                </>
              )}
            </h1>
            <div className="mt-4 text-lg lg:text-xl font-display font-bold tracking-tight" data-testid="hero-tagline">
              <span className="brand-red">{isDefault ? t("landing.hero_slogan").split(".")[0] + "." : "Craft Your Resume."}</span>{" "}
              <span className="brand-blue">{isDefault ? (t("landing.hero_slogan").split(".")[1] || "").trim() + "." : "Build Your Future."}</span>
            </div>
            <p className="mt-4 text-zinc-600 text-base lg:text-lg max-w-xl leading-relaxed">
              {isDefault ? t("landing.hero_subtitle") : v.sub}
            </p>
            <div className="mt-7 flex flex-wrap items-center gap-3">
              <Link
                to={evalLink}
                data-testid="hero-cta-primary"
                className="btn-sharp bg-[#DC1C22] hover:bg-[#A8141A] text-white"
              >
                {isDefault ? t("landing.hero_cta_primary") : v.cta} <ArrowUpRight className="w-4 h-4" />
              </Link>
              <Link
                to="/history"
                data-testid="hero-cta-secondary"
                className="btn-sharp border border-zinc-300 hover:bg-zinc-900 hover:text-white"
              >
                {t("landing.hero_cta_secondary")}
              </Link>
            </div>
            <div className="mt-8 flex items-center gap-6">
              <div className="flex -space-x-2">
                <img className="w-9 h-9 ring-2 ring-white object-cover" src="https://images.unsplash.com/photo-1655249493799-9cee4fe983bb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzV8MHwxfHNlYXJjaHwzfHxwcm9mZXNzaW9uYWwlMjBidXNpbmVzcyUyMGhlYWRzaG90JTIwcG9ydHJhaXR8ZW58MHx8fHwxNzc5Mjk1MDIzfDA&ixlib=rb-4.1.0&q=85" alt="user" />
                <img className="w-9 h-9 ring-2 ring-white object-cover" src="https://images.unsplash.com/photo-1655249481446-25d575f1c054?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzV8MHwxfHNlYXJjaHwyfHxwcm9mZXNzaW9uYWwlMjBidXNpbmVzcyUyMGhlYWRzaG90JTIwcG9ydHJhaXR8ZW58MHx8fHwxNzc5Mjk1MDIzfDA&ixlib=rb-4.1.0&q=85" alt="user" />
                <img className="w-9 h-9 ring-2 ring-white object-cover" src="https://images.pexels.com/photos/37148308/pexels-photo-37148308.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" alt="user" />
              </div>
              <div className="text-sm text-zinc-500">
                {t("landing.trust_line")} <span className="text-zinc-900 font-medium">Google, Stripe, McKinsey</span> & more.
              </div>
            </div>
          </div>
          <div className="lg:col-span-5 relative fade-up delay-2">
            <div className="relative aspect-[4/5] overflow-hidden bg-white border border-zinc-100">
              <img
                src="https://customer-assets.emergentagent.com/job_resume-refine-10/artifacts/55prsd04_file_0000000091f471f4bd5d37a8933cba8f.png"
                alt="ResumeLab — Craft Your Resume. Build Your Future."
                className="absolute inset-0 w-full h-full object-contain p-6"
              />
            </div>
            <div className="absolute -bottom-6 -left-6 bg-white border border-zinc-200 p-5 w-[260px] shadow-sm">
              <div className="overline mb-2">RESUMELAB SCORE</div>
              <div className="flex items-end gap-3">
                <span className="font-display font-extrabold text-5xl brand-red">87</span>
                <span className="text-xs text-zinc-500 mb-2">/ 100</span>
              </div>
              <div className="mt-3 h-1 bg-zinc-100">
                <div className="h-full bg-brand-red" style={{ width: "87%" }} />
              </div>
              <div className="mt-3 text-xs text-zinc-500">+22 pts vs. first draft</div>
            </div>
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="border-b border-zinc-200">
        <div className="max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-4">
          {STATS.map((s, i) => (
            <div
              key={s.label}
              data-testid={`stat-${i}`}
              className={`px-6 py-8 lg:px-10 lg:py-12 ${i !== STATS.length - 1 ? "border-r border-zinc-200" : ""}`}
            >
              <div className="overline">{s.label}</div>
              <div className="font-display font-extrabold text-3xl lg:text-4xl mt-3">
                <AnimatedCounter value={s.value} />
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* FEATURE PILLS — matches brand design */}
      <section className="border-b border-zinc-200 bg-gradient-to-r from-red-50/40 via-white to-blue-50/40">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10 lg:py-12">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-0 divide-y sm:divide-y-0 sm:divide-x divide-zinc-200">
            {FEATURE_PILLS.map((f, i) => (
              <div
                key={f.label}
                data-testid={`feature-pill-${i}`}
                className="flex items-center gap-4 px-2 sm:px-6 py-5"
              >
                <f.icon className={`w-8 h-8 ${f.color} shrink-0`} strokeWidth={1.75} />
                <span className="text-xs font-medium tracking-widest text-zinc-700 leading-tight">
                  {f.label}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CV SHOWCASE — slider + animated process */}
      <CVShowcase />

      {/* VIDEO TESTIMONIALS — Sora 2-generated two-up testimonial reel */}
      <VideoTestimonials />

      {/* REAL FEEDBACK STORIES — detailed user case studies */}
      <section className="border-b border-zinc-200/70" data-testid="feedback-stories">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-8 lg:py-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 mb-12 items-end">
            <div className="lg:col-span-7">
              <div className="overline mb-3">◆ REAL FEEDBACK</div>
              <h2 className="font-display font-extrabold text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.05]">
                Three candidates.<br />Three offers. Three weeks.
              </h2>
            </div>
            <div className="lg:col-span-5 text-zinc-600 text-base leading-relaxed lg:text-right">
              Real before-and-after scores from candidates who used ResumeLab in the last 60 days. No filters. No paid testimonials.
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 lg:gap-6">
            {FEEDBACK_STORIES.map((s, i) => (
              <div
                key={s.name}
                data-testid={`feedback-story-${i}`}
                className="bg-white/90 backdrop-blur-sm border border-zinc-200 p-6 lg:p-7 flex flex-col gap-4 hover:border-zinc-400 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-full bg-gradient-to-br from-red-500 to-orange-500 text-white flex items-center justify-center font-display font-bold text-sm shrink-0">
                    {s.avatar}
                  </div>
                  <div className="min-w-0">
                    <div className="font-display font-bold text-sm text-zinc-900 truncate">{s.name}</div>
                    <div className="text-[11px] text-zinc-500 truncate">{s.region}</div>
                  </div>
                  <div className="ml-auto flex">
                    {[...Array(5)].map((_, k) => (
                      <Star key={k} className="w-3 h-3 fill-amber-400 text-amber-400" />
                    ))}
                  </div>
                </div>
                <p className="text-xs font-mono text-zinc-700 leading-relaxed">{s.role}</p>
                <div className="flex items-center gap-3 py-3 border-y border-zinc-100">
                  <div className="text-center flex-1">
                    <div className="text-[10px] uppercase tracking-widest text-zinc-400 mb-0.5">Before</div>
                    <div className="font-mono font-bold text-xl text-zinc-400 tabular-nums">{s.scoreBefore}</div>
                  </div>
                  <ArrowUpRight className="w-5 h-5 text-[#EF4444]" />
                  <div className="text-center flex-1">
                    <div className="text-[10px] uppercase tracking-widest text-zinc-400 mb-0.5">After</div>
                    <div className="font-mono font-bold text-xl text-[#007A5E] tabular-nums">{s.scoreAfter}</div>
                  </div>
                </div>
                <div className="relative">
                  <Quote className="absolute -top-1 -left-1 w-4 h-4 text-zinc-300" />
                  <p className="text-sm text-zinc-700 leading-relaxed pl-5 italic">"{s.quote}"</p>
                </div>
                <div className="mt-auto pt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-[#007A5E]">
                  <CheckCircle2 className="w-3.5 h-3.5" />
                  {s.win}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* LIVE DEMO WIDGET */}
      <LiveDemoWidget />

      {/* PILLARS */}
      <section className="border-b border-zinc-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-8 lg:py-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-10">
            <div className="lg:col-span-5">
              <div className="overline mb-4">◆ THE SYSTEM</div>
              <h2 className="font-display font-extrabold text-4xl lg:text-5xl tracking-tight leading-[1.05]">
                Not a checker.<br />A coaching engine.
              </h2>
            </div>
            <div className="lg:col-span-6 lg:col-start-7 text-zinc-600 text-lg leading-relaxed">
              Most resume tools count buzzwords. ResumeLab measures whether your resume actually
              convinces a hiring committee — then tells you, line by line, how to make it stronger.
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 grid-borders border-t border-l border-zinc-200/70">
            {PILLARS.map((p, i) => (
              <div key={p.title} data-testid={`pillar-${i}`} className="p-8 lg:p-10 group">
                <p.icon className="w-7 h-7 text-[#DC1C22] mb-5" strokeWidth={1.75} />
                <h3 className="font-display font-extrabold text-2xl mb-3">{p.title}</h3>
                <p className="text-zinc-600 leading-relaxed">{p.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* COMPARISON — ResumeLab vs ChatGPT vs Indeed */}
      <CompareTable />

      {/* PRICING CTA — moved up to follow the comparison directly (was buried below Before/After). */}
      <section id="pricing-cta" className="border-b border-zinc-200/70 bg-gradient-to-br from-zinc-900 via-zinc-900 to-red-950 text-white" data-testid="pricing-cta">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-8 lg:py-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 mb-5">
                <Tag className="w-4 h-4 text-amber-400" />
                <span className="font-mono text-xs tracking-widest text-amber-400 uppercase">◆ PRICING · $20 / month</span>
              </div>
              <h2 className="font-display font-extrabold text-4xl lg:text-5xl tracking-tight leading-[1.05] mb-5">
                One audit costs less than<br />a coffee. One offer pays<br /><span className="text-amber-400">for itself for life.</span>
              </h2>
              <p className="text-zinc-300 text-base lg:text-lg leading-relaxed max-w-xl mb-7">
                Unlimited AI audits, line-by-line rewrites, cover letters, ATS scoring, and PDF / Word exports — $20/month. Cancel anytime. M-Pesa, Visa & Mastercard accepted.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Link
                  to="/pricing"
                  data-testid="home-pricing-cta-primary"
                  className="inline-flex items-center justify-center gap-2 bg-amber-400 hover:bg-amber-300 text-zinc-900 px-6 py-3.5 text-sm font-bold tracking-wider uppercase transition-colors"
                >
                  See plans & pricing <ArrowUpRight className="w-4 h-4" />
                </Link>
                <Link
                  to={evalLink}
                  data-testid="home-pricing-cta-secondary"
                  className="inline-flex items-center justify-center gap-2 border border-white/30 hover:border-white text-white px-6 py-3.5 text-sm font-semibold tracking-wider uppercase transition-colors"
                >
                  Try free audit first
                </Link>
              </div>
              <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-zinc-400">
                <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-[#22C55E]" /> Cancel anytime</span>
                <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-[#22C55E]" /> Live Stripe & M-Pesa</span>
                <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-[#22C55E]" /> Stop paying any time</span>
              </div>
            </div>
            <div className="lg:col-span-5">
              <div className="bg-white/10 backdrop-blur-sm border border-white/15 p-6 lg:p-7">
                <div className="flex items-center justify-between mb-5">
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-amber-400 mb-1">ResumeLab Pro</div>
                    <div className="font-display font-extrabold text-4xl">$20<span className="text-base text-zinc-400 font-mono">/mo</span></div>
                  </div>
                  <div className="text-right text-[11px] font-mono text-zinc-400 leading-snug">
                    Or KES 2,600 /mo<br />via M-Pesa
                  </div>
                </div>
                <ul className="space-y-2.5 text-sm">
                  {["Unlimited AI resume audits", "Line-by-line GPT-5.2 rewrites", "Cover letter generator (per JD)", "6 Word .docx templates (EN/ES/DE/FR)", "ATS scoring & keyword match", "PDF + Word exports", "Audit history & score trends"].map((f) => (
                    <li key={f} className="flex items-start gap-2 text-zinc-200">
                      <CheckCircle2 className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* STICKY JUMP NAV — appears once user scrolls past Pricing CTA. */}
      <SectionJumpNav items={JUMP_ITEMS} triggerId="pricing-cta" />

      {/* HOW IT WORKS — collapsible, open by default (most important "what is this?" answer) */}
      <CollapsibleSection
        id="sec-flow"
        title="Twelve seconds, draft to decision"
        subtitle="The 3-step flow"
        icon={Compass}
        defaultOpen
      >
        <div className="bg-zinc-50">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10 lg:py-14">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-zinc-200">
              {STEPS.map((s, i) => (
                <div key={s.n} data-testid={`step-${i}`} className="bg-white p-8 lg:p-10">
                  <div className="font-mono text-xs text-zinc-400 mb-6">{s.n} / 03</div>
                  <h3 className="font-display font-extrabold text-2xl mb-3">{s.title}</h3>
                  <p className="text-zinc-600 leading-relaxed">{s.body}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </CollapsibleSection>

      {/* BEFORE / AFTER GALLERY — collapsible */}
      <CollapsibleSection
        id="sec-before-after"
        title="Before & after — real audits"
        subtitle="See how scores jumped"
        icon={GitCompare}
      >
        <BeforeAfterGallery />
      </CollapsibleSection>

      {/* RECRUITER QUOTES — collapsible */}
      <CollapsibleSection
        id="sec-recruiters"
        title="What recruiters actually say"
        subtitle="Rotating quotes from hiring managers"
        icon={MessagesSquare}
      >
        <RecruiterQuotes />
      </CollapsibleSection>

      {/* BLOG STRIP — collapsible */}
      <CollapsibleSection
        id="sec-blog"
        title="Long-form reads from our blog"
        subtitle="Country guides, recruiter playbooks, resume tactics"
        icon={Newspaper}
      >
        <BlogStrip />
      </CollapsibleSection>


      {/* CTA */}
      <section className="bg-zinc-900 text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-8 lg:py-12 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-8">
            <div className="overline mb-4 text-zinc-400">▲ NEXT MOVE</div>
            <h2 className="font-display font-extrabold text-4xl lg:text-6xl tracking-tight leading-[1.0]">
              Stop guessing.<br />Start landing.
            </h2>
            <p className="mt-6 text-zinc-400 text-lg max-w-xl">
              Get a recruiter-grade audit in under a minute. Free for your first resume.
            </p>
          </div>
          <div className="lg:col-span-4 lg:text-right">
            <Link
              to="/evaluate"
              data-testid="footer-cta"
              className="btn-sharp bg-white text-zinc-900 hover:bg-[#DC1C22] hover:text-white"
            >
              Run my audit <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
      <ExitIntentModal />
    </main>
  );
}
